import { useState } from 'react';
import { Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert, Snackbar, FormControlLabel, Switch, Chip, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, Typography, Divider } from '@mui/material';
import { CallReceived, CallMade, Undo, SwapHoriz, Tune } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { TableToolbar } from '../../components/TableToolbar';
import { StatusBadge } from '../../components/StatusBadge';
import { inventoryApi, warehousesApi, itemsApi, type StockTxn } from '../../api/inventory.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

type Mode = 'receipt' | 'issue' | 'return' | 'transfer' | 'adjust';
interface FormState {
  itemId: number | ''; warehouseId: number | ''; toWarehouseId: number | ''; quantity: number; unitCost: number | '';
  txnType: string; direction: 'in' | 'out'; returnType: string; reserve: boolean;
  projectId: string; siteId: string; omsId: string; workOrderId: string; technicianId: string;
  referenceCode: string; txnDate: string; narration: string; serials: string;
}
const blank: FormState = { itemId: '', warehouseId: '', toWarehouseId: '', quantity: 0, unitCost: '', txnType: 'purchase_receipt', direction: 'in', returnType: 'unused', reserve: false, projectId: '', siteId: '', omsId: '', workOrderId: '', technicianId: '', referenceCode: '', txnDate: '', narration: '', serials: '' };

const TXN_TYPES = ['opening_stock', 'purchase_receipt', 'material_issue', 'material_return', 'stock_transfer', 'adjustment', 'scrap'];
const parseSerials = (s: string): string[] | undefined => { const a = s.split(/[\s,\n]+/).map((x) => x.trim()).filter(Boolean); return a.length ? a : undefined; };
const numOrUndef = (s: string) => (s === '' ? undefined : Number(s));

export function StockLedgerPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [typeFilter, setTypeFilter] = useState('');
  const [mode, setMode] = useState<Mode | null>(null);
  const [form, setForm] = useState<FormState>(blank);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Requisition-driven material issue (procurement request → outward against required qty).
  const [reqId, setReqId] = useState<number | ''>('');
  const [planQty, setPlanQty] = useState<Record<number, string>>({});

  const ledger = useQuery({ queryKey: ['inventory', 'ledger', page, limit, typeFilter], queryFn: () => inventoryApi.ledger({ page, limit, txnType: typeFilter || undefined }) });
  const warehouses = useQuery({ queryKey: ['warehouses', 'all'], queryFn: () => warehousesApi.list({ limit: 500 }) });
  const items = useQuery({ queryKey: ['inventory-items', 'all'], queryFn: () => itemsApi.list({ limit: 1000 }) });
  const issueReqs = useQuery({ queryKey: ['issue-requisitions'], queryFn: () => inventoryApi.issueRequisitions(), enabled: mode === 'issue' });
  const plan = useQuery({
    queryKey: ['issue-plan', reqId, form.warehouseId],
    queryFn: () => inventoryApi.issuePlan(Number(reqId), form.warehouseId ? Number(form.warehouseId) : undefined),
    enabled: mode === 'issue' && !!reqId,
  });
  const itemName = (id: number) => items.data?.data.find((i) => i.id === id)?.name ?? `#${id}`;
  const whName = (id: number) => warehouses.data?.data.find((w) => w.id === id)?.name ?? `#${id}`;
  const invalidate = () => qc.invalidateQueries({ queryKey: ['inventory'] });

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const submit = useMutation({
    mutationFn: async () => {
      // Requisition-driven multi-line issue against the procurement request.
      if (issueByReq) {
        const lines = (plan.data?.lines ?? [])
          .filter((l) => l.itemId && Number(planQty[l.reqItemId] ?? 0) > 0)
          .map((l) => ({ itemId: l.itemId as number, quantity: Number(planQty[l.reqItemId]) }));
        return inventoryApi.issueAgainstRequisition({ requisitionId: Number(reqId), warehouseId: Number(form.warehouseId), siteId: numOrUndef(form.siteId), txnDate: form.txnDate || undefined, narration: form.narration || undefined, lines });
      }
      const base = { itemId: Number(form.itemId), warehouseId: Number(form.warehouseId), quantity: form.quantity, unitCost: numOrUndef(String(form.unitCost)), txnDate: form.txnDate || undefined, narration: form.narration || undefined, serials: parseSerials(form.serials) };
      if (mode === 'receipt') return inventoryApi.receipt({ ...base, txnType: form.txnType as 'opening_stock' | 'purchase_receipt', referenceCode: form.referenceCode || undefined });
      if (mode === 'issue') return inventoryApi.issue({ ...base, projectId: numOrUndef(form.projectId), siteId: numOrUndef(form.siteId), omsId: numOrUndef(form.omsId), workOrderId: numOrUndef(form.workOrderId), technicianId: numOrUndef(form.technicianId), reserve: form.reserve });
      if (mode === 'return') return inventoryApi.return({ ...base, returnType: form.returnType as 'unused' | 'defective' | 'warranty', omsId: numOrUndef(form.omsId), siteId: numOrUndef(form.siteId), workOrderId: numOrUndef(form.workOrderId) });
      if (mode === 'transfer') return inventoryApi.transfer({ itemId: Number(form.itemId), fromWarehouseId: Number(form.warehouseId), toWarehouseId: Number(form.toWarehouseId), quantity: form.quantity, unitCost: numOrUndef(String(form.unitCost)), txnDate: form.txnDate || undefined, narration: form.narration || undefined, serials: parseSerials(form.serials) });
      return inventoryApi.adjustment({ ...base, txnType: form.txnType === 'scrap' ? 'scrap' : 'adjustment', direction: form.direction });
    },
    onSuccess: () => { setMode(null); setToast('Stock transaction recorded'); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const receive = useMutation({ mutationFn: (id: number) => inventoryApi.receiveTransfer(id), onSuccess: () => { setToast('Transfer received'); invalidate(); }, onError: (e) => setToast(extractApiError(e)) });
  const confirm = useMutation({ mutationFn: (id: number) => inventoryApi.confirmReservation(id), onSuccess: () => { setToast('Reservation confirmed'); invalidate(); }, onError: (e) => setToast(extractApiError(e)) });

  const openMode = (m: Mode) => { setForm({ ...blank, txnType: m === 'receipt' ? 'purchase_receipt' : m === 'adjust' ? 'adjustment' : blank.txnType, direction: m === 'adjust' ? 'out' : 'in' }); setError(null); setReqId(''); setPlanQty({}); setMode(m); };
  const issueByReq = !!reqId && mode === 'issue';

  const columns: Column<StockTxn>[] = [
    { key: 'code', label: 'Code', width: 150 },
    { key: 'txnType', label: 'Type', render: (r) => r.txnType.replace(/_/g, ' ') },
    { key: 'direction', label: 'Dir', width: 90, render: (r) => <Chip size="small" color={r.direction === 'in' ? 'success' : 'warning'} label={r.direction === 'in' ? 'Inward' : 'Outward'} /> },
    { key: 'itemId', label: 'Item', render: (r) => itemName(r.itemId) },
    { key: 'warehouseId', label: 'Warehouse', render: (r) => whName(r.warehouseId) },
    { key: 'quantity', label: 'Qty', align: 'right' },
    { key: 'value', label: 'Value', align: 'right', render: (r) => `₹${Number(r.value).toLocaleString('en-IN')}` },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'txnDate', label: 'Date', width: 110 },
  ];

  const fields = warehouses.data?.data ?? [];
  const itemList = items.data?.data ?? [];
  const showWarehouse = mode !== null;
  const canTransact = hasPermission('inventory.transact');

  return (
    <>
      <PageHeader
        title="Stock Ledger & Operations"
        subtitle="Complete transaction history — receipts, issues, returns, transfers, adjustments and scrap."
        action={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {canTransact && <Button size="small" startIcon={<CallReceived />} variant="outlined" onClick={() => openMode('receipt')}>Receipt</Button>}
            {canTransact && <Button size="small" startIcon={<CallMade />} variant="outlined" onClick={() => openMode('issue')}>Issue</Button>}
            {canTransact && <Button size="small" startIcon={<Undo />} variant="outlined" onClick={() => openMode('return')}>Return</Button>}
            {hasPermission('inventory.transfer') && <Button size="small" startIcon={<SwapHoriz />} variant="outlined" onClick={() => openMode('transfer')}>Transfer</Button>}
            {hasPermission('inventory.adjust') && <Button size="small" startIcon={<Tune />} variant="contained" onClick={() => openMode('adjust')}>Adjust / Scrap</Button>}
          </Stack>
        }
      />

      <TableToolbar
        filters={
          <TextField select size="small" label="Type" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} sx={{ width: 200 }}>
            <MenuItem value="">All types</MenuItem>
            {TXN_TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
          </TextField>
        }
      />

      <DataTable
        columns={columns} rows={ledger.data?.data ?? []} loading={ledger.isLoading}
        page={page} limit={limit} total={ledger.data?.meta.total ?? 0} onPageChange={setPage} onLimitChange={setLimit} rowKey={(r) => r.id}
        emptyTitle="No stock movements"
        actions={(r) =>
          r.status === 'in_transit' && r.txnType === 'stock_transfer' && r.direction === 'in' && hasPermission('inventory.transact') ? (
            <Button size="small" onClick={() => receive.mutate(r.id)}>Receive</Button>
          ) : r.status === 'reserved' && hasPermission('inventory.transact') ? (
            <Button size="small" onClick={() => confirm.mutate(r.id)}>Confirm</Button>
          ) : null
        }
      />

      <Dialog open={mode !== null} onClose={() => setMode(null)} fullWidth maxWidth={issueByReq ? 'lg' : 'sm'}>
        <DialogTitle sx={{ textTransform: 'capitalize' }}>{mode === 'adjust' ? 'Adjustment / Scrap' : `Material ${mode}`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}

            {mode === 'issue' && (
              <TextField select label="Procurement Request (optional)" value={reqId}
                onChange={(e) => { setReqId(e.target.value ? Number(e.target.value) : ''); setPlanQty({}); }} fullWidth
                helperText="Select a request to issue against its line items with required qty & warehouse balance.">
                <MenuItem value="">— Ad-hoc issue (single item) —</MenuItem>
                {(issueReqs.data ?? []).map((r) => <MenuItem key={r.id} value={r.id}>{r.code}{r.projectName ? ` · ${r.projectName}` : ''}</MenuItem>)}
              </TextField>
            )}

            {!issueByReq && (
              <TextField select label="Item" value={form.itemId} onChange={(e) => set('itemId', Number(e.target.value))} fullWidth>
                {itemList.map((i) => <MenuItem key={i.id} value={i.id}>{i.code} — {i.name}{i.serialTracked ? ' (serial)' : ''}</MenuItem>)}
              </TextField>
            )}

            {mode === 'receipt' && (
              <Stack direction="row" spacing={2}>
                <TextField select label="Receipt Type" value={form.txnType} onChange={(e) => set('txnType', e.target.value)} fullWidth>
                  <MenuItem value="opening_stock">Opening Stock</MenuItem><MenuItem value="purchase_receipt">Purchase Receipt</MenuItem>
                </TextField>
                <TextField label="Reference (PO/GRN)" value={form.referenceCode} onChange={(e) => set('referenceCode', e.target.value)} fullWidth />
              </Stack>
            )}
            {mode === 'adjust' && (
              <Stack direction="row" spacing={2}>
                <TextField select label="Type" value={form.txnType} onChange={(e) => set('txnType', e.target.value)} fullWidth>
                  <MenuItem value="adjustment">Adjustment</MenuItem><MenuItem value="scrap">Scrap</MenuItem>
                </TextField>
                <TextField select label="Direction" value={form.direction} onChange={(e) => set('direction', e.target.value as 'in' | 'out')} disabled={form.txnType === 'scrap'} fullWidth>
                  <MenuItem value="in">In (+)</MenuItem><MenuItem value="out">Out (−)</MenuItem>
                </TextField>
              </Stack>
            )}

            {showWarehouse && (
              <Stack direction="row" spacing={2}>
                <TextField select label={mode === 'transfer' ? 'From Warehouse' : 'Warehouse'} value={form.warehouseId} onChange={(e) => set('warehouseId', Number(e.target.value))} fullWidth>
                  {fields.map((w) => <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>)}
                </TextField>
                {mode === 'transfer' && (
                  <TextField select label="To Warehouse" value={form.toWarehouseId} onChange={(e) => set('toWarehouseId', Number(e.target.value))} fullWidth>
                    {fields.filter((w) => w.id !== form.warehouseId).map((w) => <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>)}
                  </TextField>
                )}
              </Stack>
            )}

            {issueByReq && (
              <>
                {plan.data && (
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                    <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                      <Typography variant="body2"><b>Client:</b> {plan.data.clientName ?? '—'}{plan.data.clientCode ? ` (${plan.data.clientCode})` : ''}</Typography>
                      <Typography variant="body2"><b>Project:</b> {plan.data.projectName ?? '—'}{plan.data.projectCode ? ` (${plan.data.projectCode})` : ''}</Typography>
                      <Typography variant="body2"><b>Site:</b> {plan.data.siteName ?? '—'}{plan.data.siteCode ? ` (${plan.data.siteCode})` : ''}</Typography>
                    </Stack>
                  </Paper>
                )}
                <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Required qty vs received stock (select a warehouse to see balance)</Typography></Divider>
                {!form.warehouseId && <Alert severity="info">Select a warehouse above to see the available balance per material.</Alert>}
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, minWidth: 320 }}>Material</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Required</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Issued</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Balance (WH)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, width: 110 }}>Issue Qty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(plan.data?.lines ?? []).map((l) => {
                        const qty = planQty[l.reqItemId] ?? (l.suggestedQty ? String(l.suggestedQty) : '');
                        const over = Number(qty) > l.availableAtWarehouse;
                        return (
                          <TableRow key={l.reqItemId} hover>
                            <TableCell>
                              {l.description}
                              {!l.itemMatched && <Chip size="small" color="warning" variant="outlined" label="no stock item" sx={{ ml: 1 }} />}
                            </TableCell>
                            <TableCell align="right">{l.requiredQty}{l.unit ? ` ${l.unit}` : ''}</TableCell>
                            <TableCell align="right">{l.alreadyIssued}</TableCell>
                            <TableCell align="right">{l.availableAtWarehouse}</TableCell>
                            <TableCell align="right">
                              <TextField size="small" type="number" value={qty} disabled={!l.itemMatched || !form.warehouseId}
                                error={over} onChange={(e) => setPlanQty((p) => ({ ...p, [l.reqItemId]: e.target.value }))}
                                inputProps={{ min: 0, max: l.availableAtWarehouse, style: { textAlign: 'right' } }} sx={{ width: 100 }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {plan.data && plan.data.lines.length === 0 && <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">No line items on this requisition.</Typography></TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TextField label="Site ID (optional)" value={form.siteId} onChange={(e) => set('siteId', e.target.value)} sx={{ maxWidth: 200 }} />
              </>
            )}

            {!issueByReq && (
              <Stack direction="row" spacing={2}>
                <TextField label="Quantity" type="number" value={form.quantity} onChange={(e) => set('quantity', Number(e.target.value))} fullWidth />
                <TextField label="Unit Cost" type="number" value={form.unitCost} onChange={(e) => set('unitCost', e.target.value === '' ? '' : Number(e.target.value))} fullWidth />
              </Stack>
            )}

            {mode === 'return' && (
              <TextField select label="Return Type" value={form.returnType} onChange={(e) => set('returnType', e.target.value)} fullWidth>
                <MenuItem value="unused">Unused (back to stock)</MenuItem><MenuItem value="defective">Defective</MenuItem><MenuItem value="warranty">Warranty</MenuItem>
              </TextField>
            )}
            {((mode === 'issue' && !issueByReq) || mode === 'return') && (
              <Stack direction="row" spacing={2}>
                <TextField label="Site ID" value={form.siteId} onChange={(e) => set('siteId', e.target.value)} fullWidth />
                <TextField label="OMS ID" value={form.omsId} onChange={(e) => set('omsId', e.target.value)} fullWidth />
                <TextField label="Work Order ID" value={form.workOrderId} onChange={(e) => set('workOrderId', e.target.value)} fullWidth />
              </Stack>
            )}
            {mode === 'issue' && !issueByReq && (
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField label="Technician ID" value={form.technicianId} onChange={(e) => set('technicianId', e.target.value)} fullWidth />
                <FormControlLabel control={<Switch checked={form.reserve} onChange={(e) => set('reserve', e.target.checked)} />} label="Reserve only" />
              </Stack>
            )}

            {!issueByReq && <TextField label="Serial Numbers (comma separated — required for serial-tracked items)" value={form.serials} onChange={(e) => set('serials', e.target.value)} fullWidth multiline minRows={1} />}
            <Stack direction="row" spacing={2}>
              <TextField label="Txn Date" type="date" InputLabelProps={{ shrink: true }} value={form.txnDate} onChange={(e) => set('txnDate', e.target.value)} fullWidth />
              <TextField label="Narration" value={form.narration} onChange={(e) => set('narration', e.target.value)} fullWidth />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMode(null)}>Cancel</Button>
          <Button variant="contained"
            disabled={submit.isPending || (issueByReq
              ? (!form.warehouseId || !(plan.data?.lines ?? []).some((l) => l.itemId && Number(planQty[l.reqItemId] ?? 0) > 0))
              : (!form.itemId || !form.warehouseId || !form.quantity || (mode === 'transfer' && !form.toWarehouseId)))}
            onClick={() => submit.mutate()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
